import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { I18nextProvider, useTranslation } from "react-i18next";
import type { i18n as I18nextInstance } from "i18next";
import { useUIConfig } from "@/api/uiConfig";
import { catalogOf } from "./catalog";
import { addNamespace, createI18n, formatVars, hasNamespace } from "./instance";
import {
  BOOT_NAMESPACES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  LOCALE_QUERY_PARAM,
  SUPPORTED_LOCALES,
  type Locale,
  type Namespace,
} from "./locales";
import { readStoredLocale, resolveLocale, writeStoredLocale, type LocaleSource } from "./resolve";

/**
 * 표시 언어 컨텍스트 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * - 번역 **엔진은 i18next + react-i18next**입니다. 조회·이름공간·en 폴백·재렌더는
 *   그쪽이 하고, 이 파일은 "어느 언어인가"와 "어느 조각을 언제 올리는가"만 정합니다.
 * - 언어 결정은 `resolve.ts` 하나가 소유합니다. 여기서 규칙을 다시 쓰지 않습니다.
 * - **지원 목록이 확정되기 전에는 어떤 언어도 그리지 않습니다.** 배포가 목록을
 *   좁혔는데(예: en 전용) 그 사실이 도착하기 전에 ko를 그리면, 지원하지 않는
 *   언어가 한 프레임 보였다가 바뀝니다.
 * - 카탈로그는 **이름공간 단위 지연 로드**입니다. 조각을 못 받으면 대체 언어로,
 *   그것마저 실패하면 안정 ID로 그립니다. 폴링·자동 재시도는 없습니다.
 */
export interface I18nValue {
  locale: Locale;
  /** 이 언어가 어느 단계에서 정해졌는지. 진단·테스트가 읽습니다. */
  source: LocaleSource;
  supported: readonly Locale[];
  setLocale: (next: Locale) => void;
  /** 이름공간을 내려받도록 요청합니다. 이미 있으면 아무 일도 하지 않습니다. */
  ensure: (namespace: Namespace) => void;
  /** 활성 언어에 없는 ID를 만났을 때 대체 언어 조각을 받아 오도록 요청합니다. */
  ensureFallback: (namespace: Namespace) => void;
  /** 조각이 도착할 때마다 올라가는 세대 번호. 진단·테스트가 읽습니다. */
  generation: number;
}

/**
 * 컨텍스트를 그대로 내보냅니다. 프로덕션 경로는 아래 `I18nProvider` 하나뿐이고,
 * 격리된 컴포넌트 테스트가 언어를 고정할 때만 직접 값을 넣습니다.
 */
export const I18nContext = createContext<I18nValue | undefined>(undefined);

/**
 * Provider 없이 그려진 컴포넌트의 기본값.
 *
 * 여기서 던지면 **문구 하나 때문에 화면 전체가 죽습니다.** 기본 언어로 계속
 * 그리는 편이 언제나 낫습니다. 앱은 main.tsx에서 Provider를 한 번 붙이므로
 * 이 경로는 사실상 안전망입니다.
 */
const DETACHED: I18nValue = {
  locale: DEFAULT_LOCALE,
  source: "fallback",
  supported: SUPPORTED_LOCALES,
  setLocale: () => {},
  ensure: () => {},
  ensureFallback: () => {},
  generation: 0,
};

export function useI18n(): I18nValue {
  return useContext(I18nContext) ?? DETACHED;
}

/** 메시지 인자. 숫자는 언어 규칙으로 표기되고, 문자열은 **그대로** 들어갑니다. */
export type MessageVars = Record<string, string | number>;

export type Translate = (id: string, vars?: MessageVars) => string;

/**
 * 이름공간 하나의 번역 함수.
 *
 * 조회는 react-i18next의 `useTranslation(namespace)`가 합니다 — 활성 언어에 ID가
 * 없으면 i18next가 `fallbackLng: "en"`으로 내려가고, 그것도 없으면 **ID 자체**를
 * 돌려줍니다(빈 문자열을 돌려주면 사라진 문구가 조용한 빈칸이 됩니다).
 * 대체 언어 조각은 그 miss가 실제로 일어난 뒤에만 내려받습니다.
 */
export function useT(namespace: Namespace): { t: Translate; ready: boolean } {
  const { locale, ensure, ensureFallback, generation } = useI18n();
  const { t: translate, i18n } = useTranslation(namespace);

  useEffect(() => {
    ensure(namespace);
  }, [ensure, namespace, locale]);

  return useMemo(() => {
    void generation;
    /* Provider가 없으면 react-i18next가 기능이 빈 대역을 돌려줍니다(문서화된 동작).
       그때도 화면이 죽지 않도록 조회 가능 여부를 먼저 확인합니다. */
    const has = (lng: string) => typeof i18n.hasResourceBundle === "function" && i18n.hasResourceBundle(lng, namespace);
    const loaded = has(locale);
    const t: Translate = (id, vars) => {
      if (!loaded && !has(FALLBACK_LOCALE)) {
        /* 조각이 아직 없거나 못 받은 경우입니다. 대체 언어를 받아 오게 하고,
           도착하면 react-i18next가 이 컴포넌트를 다시 그립니다. */
        ensureFallback(namespace);
        return id;
      }
      return translate(id, { ...formatVars(locale, vars), defaultValue: id });
    };
    return { t, ready: loaded };
  }, [translate, i18n, locale, namespace, generation, ensureFallback]);
}

export interface I18nProviderProps {
  children: ReactNode;
  /** 테스트가 언어를 고정할 때만 씁니다. 프로덕션 경로는 URL·저장값·브라우저를 봅니다. */
  forcedLocale?: Locale;
  /** 테스트가 인스턴스를 들여다볼 때만 씁니다. 기본은 이 Provider가 만든 것입니다. */
  instance?: I18nextInstance;
}

export function I18nProvider({ children, forcedLocale, instance }: I18nProviderProps) {
  const [params, setParams] = useSearchParams();
  const config = useUIConfig();
  const [generation, setGeneration] = useState(0);
  const [i18n] = useState(() => instance ?? createI18n());
  /* 저장값은 마운트 시점에 한 번 읽고, 사용자가 고를 때 함께 갱신합니다. 저장소만
     쓰고 상태를 두면 URL에서 `lang`이 사라지는 순간 브라우저 언어로 되돌아갑니다. */
  const [stored, setStored] = useState<string | null>(() => readStoredLocale());

  /**
   * 지원 목록의 **권위 있는 값**은 서버 응답입니다. 아직 모르면 언어를 고르지
   * 않습니다. 요청이 끝내 실패하면 그때만 컴파일 타임 닫힌 목록으로 내려갑니다 —
   * 매달리거나 폴링하지 않습니다.
   */
  const supportedKnown = config.data !== undefined || config.isError;
  const supported = config.data?.supportedLocales ?? SUPPORTED_LOCALES;
  const langParam = params.get(LOCALE_QUERY_PARAM);
  const decision = useMemo(() => {
    if (forcedLocale) return { locale: forcedLocale, source: "url" as LocaleSource };
    return resolveLocale({
      search: langParam ? `${LOCALE_QUERY_PARAM}=${encodeURIComponent(langParam)}` : "",
      stored,
      browser: typeof navigator === "undefined" ? [] : navigator.languages,
      operatorDefault: config.data?.defaultLocale,
      supported,
    });
  }, [forcedLocale, langParam, stored, config.data, supported]);

  const { locale: target, source } = decision;
  const resolvable = forcedLocale !== undefined || supportedKnown;

  /**
   * 화면이 실제로 쓰는 언어. **조각이 도착한 뒤에만** 바뀝니다.
   *
   * 목표 언어로 즉시 갈아타면 카탈로그가 없는 프레임이 생기고, 그때 children을
   * 언마운트해 기다리면 **화면 전체가 다시 마운트**됩니다 — 포커스·스크롤·열린
   * 패널이 사라집니다. 준비된 언어로 계속 그리다가 한 번에 바꿉니다.
   */
  const [locale, setReadyLocale] = useState<Locale | undefined>(undefined);

  const bump = useCallback(() => setGeneration((n) => n + 1), []);

  /**
   * 대체 언어 조각을 받아 옵니다. 활성 언어가 이미 대체 언어면 할 일이 없습니다.
   * 실패해도 조용히 멈춥니다 — 여기서 던지면 화면 전체가 죽습니다.
   */
  const ensureFallback = useCallback(
    (namespace: Namespace) => {
      if (locale === FALLBACK_LOCALE || hasNamespace(i18n, FALLBACK_LOCALE, namespace)) return;
      void addNamespace(i18n, FALLBACK_LOCALE, namespace).then(bump, () => {});
    },
    [i18n, locale, bump],
  );

  const ensure = useCallback(
    (namespace: Namespace) => {
      if (locale === undefined || hasNamespace(i18n, locale, namespace)) return;
      /* 조각을 못 받으면 대체 언어로 한 번 내려갑니다. 그래야 그 화면이 ID가 아니라
         읽을 수 있는 문구로 뜹니다. */
      void addNamespace(i18n, locale, namespace).then(bump, () => ensureFallback(namespace));
    },
    [i18n, locale, bump, ensureFallback],
  );

  useEffect(() => {
    if (!resolvable) return undefined;
    let cancelled = false;
    const settle = (next: Locale) => {
      if (cancelled) return;
      void i18n.changeLanguage(next);
      setGeneration((n) => n + 1);
      setReadyLocale(next);
    };
    if (BOOT_NAMESPACES.every((ns) => hasNamespace(i18n, target, ns) || catalogOf(target, ns) !== undefined)) {
      void Promise.all(BOOT_NAMESPACES.map((ns) => addNamespace(i18n, target, ns))).then(
        () => settle(target),
        () => settle(target),
      );
      return () => {
        cancelled = true;
      };
    }
    const boot = (value: Locale) => Promise.all(BOOT_NAMESPACES.map((ns) => addNamespace(i18n, value, ns)));
    /* 조각을 못 받아도 화면은 떠야 합니다. 활성 언어가 실패하면 대체 언어를 한 번
       시도하고, 그것마저 실패하면 **결정적인 안전 화면**으로 넘어갑니다 —
       문구는 ID로 보이지만 빈 화면이 영원히 남지는 않습니다. 재시도·폴링은 없습니다. */
    void boot(target).then(
      () => settle(target),
      () => {
        if (cancelled) return;
        if (target === FALLBACK_LOCALE) {
          settle(target);
          return;
        }
        void boot(FALLBACK_LOCALE).then(
          () => settle(FALLBACK_LOCALE),
          () => settle(target),
        );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [i18n, target, resolvable]);

  const chooseLocale = useCallback(
    (next: Locale) => {
      writeStoredLocale(next);
      /* 저장소만 쓰고 상태를 그대로 두면, URL의 `lang`이 사라지는 순간(SPA 이동)
         브라우저 언어로 되돌아갑니다. */
      setStored(next);
      /* 라우트·쿼리·필터는 그대로 둡니다 — `lang` 하나만 바뀝니다. replace라 뒤로
         가기 기록에 언어 전환이 쌓이지 않습니다. */
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set(LOCALE_QUERY_PARAM, next);
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const value = useMemo<I18nValue>(
    () => ({ locale: locale ?? target, source, supported, setLocale: chooseLocale, ensure, ensureFallback, generation }),
    [locale, target, source, supported, chooseLocale, ensure, ensureFallback, generation],
  );

  return (
    <I18nextProvider i18n={i18n}>
      <I18nContext.Provider value={value}>
        {locale !== undefined ? children : <div className="state" aria-busy="true" data-testid="i18n-boot" />}
      </I18nContext.Provider>
    </I18nextProvider>
  );
}
